import React from 'react';
import styles from '../../styles.css';
import quickSelectionStyles from './styles.css';
import QuickSelectDropdown from '../QuickSelectDropdown/index.jsx';

import { isAllChildrensSelected } from '../../utils';
import { useTranslation } from 'react-i18next';

export default function QuickSelectionButtons({
	mainSubjectList,
	selectedSubjects,
	addSelectedSubject,
	quickSelectionDescription,
	nestedListsToShow,
	showNestedLists,
}) {
	let allMeasures = ['GOVERNANCE'];
	let allCategories = [];
	let allSubCategories = [];
	let allIndicators = [];
	let allIndicatorsWithSubSubIndicators = [];
	let allSubIndicators = [];
	let allSubSubIndicators = [];

	const mainSubjectListWithoutCitVoices = mainSubjectList.filter((subject) => subject.id !== 'CITVOICE');
	const { t } = useTranslation();

	const getDependantChildrens = (list) => {
		list.forEach((child) => {
			if (child.childrens) {
				getDependantChildrens(child.childrens);
			}
			if (child.itemType === 'Categories') {
				allCategories.push(child.id);
				allMeasures.push(child.id);
			}
			if (child.itemType === 'SubCategories') {
				allSubCategories.push(child.id);
				allMeasures.push(child.id);
			}
			if (child.itemType === 'Indicators') {
				allIndicators.push(child.id);
				allMeasures.push(child.id);
				if (child.childrens) {
					child.childrens.forEach((subChild) => {
						if (subChild.childrens && subChild.childrens.length) {
							allIndicatorsWithSubSubIndicators.push(child.id);
						}
					});
				}
			}
			if (child.itemType === 'SubIndicators') {
				allSubIndicators.push(child.id);
				allMeasures.push(child.id);
			}
			if (child.itemType === 'SubSubIndicators') {
				allSubSubIndicators.push(child.id);
				allMeasures.push(child.id);
			}
		});
	};

	getDependantChildrens(mainSubjectListWithoutCitVoices);

	const optionMap = {
		measures: {
			addIds: allMeasures,
			removeIds: ['GOVERNANCE', '_1', '_2', '_3', '_4', ...allMeasures],
		},
		categories: {
			addIds: allCategories,
			removeIds: ['_1', ...allCategories],
		},
		subCategories: {
			addIds: allSubCategories,
			removeIds: ['_2', ...allSubCategories],
		},
		indicators: {
			addIds: allIndicators,
			removeIds: ['_3', ...allIndicators],
		},
		subIndicators: {
			addIds: allSubIndicators,
			removeIds: ['_4', ...allSubIndicators],
		},
		subSubIndicators: {
			addIds: allSubSubIndicators,
			removeIds: ['_4', ...allSubSubIndicators],
		},
	};

	const options = [
		{ key: 'measures', label: t('All Measures'), count: allMeasures.length },
		{ key: 'categories', label: t('All Categories'), count: allCategories.length },
		{ key: 'subCategories', label: t('All Sub-Categories'), count: allSubCategories.length },
		{ key: 'indicators', label: t('All Indicators'), count: allIndicators.length },
		{ key: 'subIndicators', label: t('All Sub-Indicators'), count: allSubIndicators.length },
		{ key: 'subSubIndicators', label: t('All Sub-Sub-Indicators'), count: allSubSubIndicators.length },
	];

	const initialCheckedKeys = new Set();
	options.forEach((option) => {
		const map = optionMap[option.key];
		if (map && isAllChildrensSelected(map.addIds, selectedSubjects)) {
			initialCheckedKeys.add(option.key);
		}
	});

	const handleConfirm = (checkedKeys, uncheckedKeys) => {
		const subjectsToAdd = new Set();
		const subjectsToRemove = new Set();

		checkedKeys.forEach((key) => {
			if (!initialCheckedKeys.has(key)) {
				optionMap[key]?.addIds.forEach((id) => subjectsToAdd.add(id));
			}
		});

		uncheckedKeys.forEach((key) => {
			// Skip the master: it auto-unticks whenever a child is unticked, so honouring its
			// removeIds would wipe every measure when the user only unchecked one child. Each
			// child still contributes its own removeIds, which covers the deliberate-clear case.
			if (key === 'measures') return;
			optionMap[key]?.removeIds.forEach((id) => subjectsToRemove.add(id));
		});

		// Anything we're adding takes priority over removing.
		subjectsToAdd.forEach((id) => subjectsToRemove.delete(id));

		if (subjectsToAdd.size > 0 || subjectsToRemove.size > 0) {
			addSelectedSubject({
				subjectsGroup: [...subjectsToAdd],
				subjectsToRemove: [...subjectsToRemove],
				selectedSubjects,
			});
		}

		const nextNestedListsToShow = new Set(nestedListsToShow);

		if (uncheckedKeys.has('subIndicators')) {
			nextNestedListsToShow.forEach((subject) => {
				if (!allIndicators.includes(subject)) {
					nextNestedListsToShow.delete(subject);
				}
			});
		}
		if (uncheckedKeys.has('subSubIndicators')) {
			nextNestedListsToShow.forEach((subject) => {
				if (!allSubIndicators.includes(subject) && !allIndicatorsWithSubSubIndicators.includes(subject)) {
					nextNestedListsToShow.delete(subject);
				}
			});
		}

		if (checkedKeys.has('subIndicators') && !initialCheckedKeys.has('subIndicators')) {
			allIndicators.forEach((indicator) => nextNestedListsToShow.add(indicator));
		}
		if (checkedKeys.has('subSubIndicators') && !initialCheckedKeys.has('subSubIndicators')) {
			allIndicatorsWithSubSubIndicators.forEach((indicator) => nextNestedListsToShow.add(indicator));
			allSubIndicators.forEach((indicator) => nextNestedListsToShow.add(indicator));
		}

		showNestedLists([...nextNestedListsToShow]);
	};

	return (
		<div className={quickSelectionStyles.measureQuick}>
			<QuickSelectDropdown
				options={options}
				initialCheckedKeys={initialCheckedKeys}
				onConfirm={handleConfirm}
				masterKey="measures"
			/>
			<img
				onClick={() => quickSelectionDescription.onClick()}
				className={styles.informationIcon}
				src="/img/icons/info_icon.svg"
				alt="Info"
				width="17"
				height="17"
			/>
		</div>
	);
}
